import { type ParsedContact, makeContact } from "../contact";
import { headerReader } from "../csv";

/**
 * The columns that identify a Google Contacts export (R2). Each group must
 * match at least one column, because Google renamed Given/Family Name to
 * First/Last Name and both namings are still exported in the wild. Requiring
 * only the older naming would reject a fresh export outright.
 */
export const GOOGLE_HEADER_GROUPS = [
	["E-mail 1 - Value"],
	["Given Name", "First Name"],
] as const;

/**
 * Google prefixes its own system groups with `* ` and stamps `* myContacts`
 * on every row, so they are membership bookkeeping rather than labels the user
 * chose. Only the user's own labels become tags.
 */
const isSystemLabel = (label: string): boolean => label.startsWith("* ");

/** Google packs repeated values into one cell with this literal separator. */
const MULTI = " ::: ";

const split = (cell: string): string[] =>
	cell
		.split(MULTI)
		.map((value) => value.trim())
		.filter((value) => value !== "");

const columns = (
	header: readonly string[],
	row: readonly string[],
	pattern: RegExp,
): string[] =>
	header.flatMap((name, index) =>
		pattern.test(name) ? split(row[index] ?? "") : [],
	);

const EMAIL = /^E-?mail \d+ - Value$/i;
const PHONE = /^Phone \d+ - Value$/i;

export const googleContacts = (
	header: readonly string[],
	rows: readonly (readonly string[])[],
): ParsedContact[] => {
	const read = headerReader(header);
	return rows.map((row, index) => {
		// Older exports use Given/Family Name, newer ones First/Last Name.
		const parts = [
			read(row, "Given Name") || read(row, "First Name"),
			read(row, "Family Name") || read(row, "Last Name"),
		].filter((part) => part !== "");
		return makeContact(index + 1, {
			name: read(row, "Name") || parts.join(" "),
			// Older exports number the organisation and address groups; current
			// ones drop the number. Read whichever this file carries.
			organisation:
				read(row, "Organization 1 - Name") || read(row, "Organization Name"),
			position:
				read(row, "Organization 1 - Title") || read(row, "Organization Title"),
			emails: columns(header, row, EMAIL),
			phones: columns(header, row, PHONE),
			location:
				read(row, "Address 1 - Formatted") || read(row, "Address 1 - Street"),
			note: read(row, "Notes"),
			// KTD10: group labels become tags once whitespace is hyphenated.
			// Older exports call the column Group Membership, current ones Labels.
			labels: split(
				read(row, "Group Membership") || read(row, "Labels"),
			).filter((label) => !isSystemLabel(label)),
		});
	});
};
